import {
  useEffect,
  useLayoutEffect,
  Suspense,
  useDeferredValue,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useUnseenContent, useMarkSeenByEntity } from '../notifications/api'
import { useLocation, useSearchParams } from 'react-router'
import {
  useDisclosure,
  useMediaQuery,
  usePagination,
  useWindowScroll,
} from '@mantine/hooks'
import { Carousel } from '@mantine/carousel'
import { useArenaPosts } from './api'
import Meta from '../common/Meta'
import Header from '../common/components/header/Header'
import RoarPost from './RoarPost'
import CreatePost from './CreatePost'
import PostDetails from './PostDetails'
import classes from './Arena.module.css'
import clsx from 'clsx'
import TopPosters from './TopPosters'
import ArenaPostFilters, {
  ArenaPostsFilterType,
  ArenaSearchInput,
} from './ArenaFilters'
import { Typography } from '../common/typography'
import {
  ActionIcon,
  Alert,
  Drawer,
  LoadingOverlay,
  Pagination,
  Stack,
} from '@mantine/core'
import { IconAdjustmentsHorizontal, IconInfoCircle } from '@tabler/icons-react'
import Tabs from '../stories/Tabs/Tabs'
import RoarPostCover from './RoarPostCover'
import { useCompetitionId } from '../common/hooks'

export default function Arena() {
  const isDesktop = useMediaQuery('(min-width: 800px)')
  const [editingPostId, setEditingPostId] = useState<number | undefined>()
  const [scroll] = useWindowScroll()
  const isScrolled = useMemo(() => scroll.y > 1, [scroll.y])
  const [author, setAuthor] = useState<number | undefined>(undefined)
  const [dateRange, setDateRange] = useState<[string | null, string | null]>([
    null,
    null,
  ])
  const [filter, setFilter] = useState<ArenaPostsFilterType>({})
  const [search, setSearch] = useState<string | undefined>()
  const tabs = [
    {
      value: 'all',
      label: 'All',
    },
    {
      value: 'posts',
      label: 'Posts',
    },
    {
      value: 'roars',
      label: 'Roars',
    },
  ]
  const [activeTab, setActiveTab] = useState<string>('all')
  const [opened, { open, close }] = useDisclosure(false)

  const handleFilterChange = (updates: Partial<ArenaPostsFilterType>) => {
    setFilter((prev) => ({
      ...prev,
      ...updates,
    }))
  }

  return (
    <div className={classes.container}>
      <Meta title="Arena" />
      <Header title="Arena" />

      <div className={classes.content}>
        <div className={classes.contentMain}>
          <CreatePost isEditing={editingPostId} isScrolled={isScrolled} />

          <div
            className={clsx(classes.arenaPosts, {
              [classes.arenaPostsMobile]: !isDesktop,
            })}
          >
            <div className={classes.contentHeader}>
              <Tabs
                items={tabs}
                value={activeTab}
                onChange={setActiveTab}
                isSmall
              />

              <div className={classes.filterContainer}>
                <ArenaSearchInput
                  onSearch={(value) => {
                    setSearch(value)
                  }}
                />

                {!isDesktop && (
                  <ActionIcon
                    variant="outline"
                    className={classes.filterActionIcon}
                    onClick={open}
                  >
                    <IconAdjustmentsHorizontal className={classes.filterIcon} />
                  </ActionIcon>
                )}
              </div>
            </div>

            {search && (
              <Typography
                level={2}
                fs="xl"
                fw={500}
                lh="125"
                fc={{ light: 'black', dark: 'gray-2' }}
              >
                {`Searched for: "${search}"`}
              </Typography>
            )}

            <LoadingArenaPostsList
              filter={{ ...filter, search }}
              editingPostId={editingPostId}
              setEditingPostId={setEditingPostId}
              active_tab={activeTab}
            />
            <Drawer
              opened={opened}
              onClose={close}
              position="bottom"
              size="xs"
              title="Filters"
              classNames={{
                header: classes.drawerHeader,
                content: classes.drawerContent,
              }}
            >
              <div className={classes.arenaFiltersMobile}>
                <ArenaPostFilters
                  onChange={handleFilterChange}
                  author={author}
                  setAuthor={setAuthor}
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                />
              </div>
            </Drawer>
          </div>
        </div>

        {isDesktop && (
          <div className={classes.contentSidebar}>
            <div className={classes.arenaFilters}>
              <Typography level={5} fw={500} lh="125">
                Filters
              </Typography>
              <ArenaPostFilters
                onChange={handleFilterChange}
                author={author}
                setAuthor={setAuthor}
                dateRange={dateRange}
                setDateRange={setDateRange}
              />
            </div>
            <TopPosters />
          </div>
        )}
      </div>
    </div>
  )
}

const shallowCompare = (
  // eslint-disable-next-line
  obj1: { [k: string]: any },
  // eslint-disable-next-line
  obj2: { [k: string]: any }
) =>
  Object.keys(obj1).length === Object.keys(obj2).length &&
  Object.keys(obj1).every((key) => obj1[key] === obj2[key])

function LoadingArenaPostsList({
  filter,
  active_tab,
  editingPostId,
  setEditingPostId,
}: {
  filter: Partial<ArenaPostsFilterType>
  active_tab: string
  editingPostId?: number
  setEditingPostId: React.Dispatch<React.SetStateAction<number | undefined>>
}) {
  const deferredFilter = useDeferredValue(filter)
  const isStale = !shallowCompare(deferredFilter, filter)

  return (
    <Suspense
      fallback={<LoadingOverlay visible overlayProps={{ opacity: 0 }} />}
    >
      <LoadingOverlay visible={isStale} overlayProps={{ opacity: 0 }} />
      <ArenaPostsList
        filter={deferredFilter}
        editingPostId={editingPostId}
        setEditingPostId={setEditingPostId}
        active_tab={active_tab}
      />
    </Suspense>
  )
}

function ArenaPostsList({
  filter,
  active_tab,
  editingPostId,
  setEditingPostId,
}: {
  filter: Partial<ArenaPostsFilterType>
  active_tab: string
  editingPostId?: number
  setEditingPostId: React.Dispatch<React.SetStateAction<number | undefined>>
}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const currentPage = Number(searchParams.get('page')) || 1
  const pageSize = 20
  const competition_id = useCompetitionId()

  const { data: unseenContent } = useUnseenContent()
  const { mutate: markSeenByEntity } = useMarkSeenByEntity()
  const [displayedRoarIds, setDisplayedRoarIds] = useState<number[]>([])

  const location = useLocation()
  const targetRef = useRef<HTMLDivElement>(null)
  const [initialCommentToScroll, setInitialCommentToScroll] = useState<
    number | null
  >(null)

  const hash = location.hash.substring(1)
  let postIdToScroll = null
  let commentIdToScroll = null

  if (hash.startsWith('post-')) {
    const parts = hash.split('-')
    postIdToScroll = parseInt(parts[1])
    if (parts[2] === 'comment') {
      commentIdToScroll = parseInt(parts[3])
    }
  }

  useLayoutEffect(() => {
    if (targetRef.current && postIdToScroll) {
      targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [postIdToScroll])

  useEffect(() => {
    if (commentIdToScroll) {
      setInitialCommentToScroll(commentIdToScroll)
    }
  }, [commentIdToScroll])

  const { data: arenaPosts } = useArenaPosts({
    competition_id: competition_id,
    page: currentPage,
    page_size: pageSize,
    ...filter,
  })

  let filteredPosts = arenaPosts.posts

  if (active_tab === 'posts') {
    filteredPosts = arenaPosts.posts.filter((post) => !post.is_roar)
  } else if (active_tab === 'roars') {
    filteredPosts = arenaPosts.posts.filter((post) => post.is_roar)
  }

  useEffect(() => {
    if (active_tab === 'all' && unseenContent && filteredPosts.length > 0) {
      const unseenRoarPosts = filteredPosts.filter(
        (post) => post.is_roar && unseenContent.post_ids.includes(post.post_id)
      )

      setDisplayedRoarIds((prev) => {
        const newRoarIds = unseenRoarPosts
          .map((p) => p.post_id)
          .filter((id) => !prev.includes(id))

        return newRoarIds.length > 0 ? [...prev, ...newRoarIds] : prev
      })
    }
  }, [active_tab, unseenContent, filteredPosts])

  const totalPages = Math.ceil(arenaPosts.total / pageSize)

  const pagination = usePagination({
    total: totalPages,
    initialPage: currentPage,
    siblings: 1,
  })

  const handlePageChange = (page: number) => {
    setSearchParams({ page: page.toString() })
    pagination.setPage(page)
  }

  const roarPosts =
    active_tab === 'all'
      ? filteredPosts.filter(
          (post) => post.is_roar && displayedRoarIds.includes(post.post_id)
        )
      : filteredPosts.filter((post) => post.is_roar)

  const roarCount = roarPosts.length

  const handleSlideChange = (index: number) => {
    if (active_tab !== 'all') return
    if (index > 0 && roarPosts[index - 1]) {
      const post = roarPosts[index - 1]
      markSeenByEntity({
        entities: [{ entity_type: 'post', entity_id: post.post_id }],
      })
    }
  }

  let postsList: React.ReactNode | null = null

  if (filteredPosts.length === 0) {
    const message = filter.search
      ? 'No posts found for your search'
      : 'No posts available'
    postsList = (
      <Stack maw={1000} w="full" h={200} align="center" justify="center">
        <Alert
          classNames={{ root: classes.alertRoot, icon: classes.alertIcon }}
          icon={<IconInfoCircle />}
        >
          {message}
        </Alert>
      </Stack>
    )
  } else {
    postsList = (
      <div className={classes.posts}>
        {active_tab === 'roars' ? (
          filteredPosts
            .filter((post) => post.is_roar)
            .map((post) => (
              <RoarPost
                key={post.post_id}
                post_id={post.post_id}
                arenaPost={post}
                isEditing={editingPostId}
                showCommentsInline={true}
                ref={post.post_id === postIdToScroll ? targetRef : null}
                initialCommentToScroll={
                  post.post_id === postIdToScroll
                    ? initialCommentToScroll
                    : null
                }
              />
            ))
        ) : (
          <>
            {active_tab === 'all' && roarCount >= 1 && (
              <Carousel
                withIndicators
                withControls={false}
                height={200}
                classNames={{
                  indicators: classes.indicators,
                }}
                onSlideChange={handleSlideChange}
              >
                <Carousel.Slide>
                  <RoarPostCover posts={roarPosts} />
                </Carousel.Slide>

                {roarPosts.map((post) => (
                  <Carousel.Slide key={post.post_id}>
                    <RoarPost
                      post_id={post.post_id}
                      arenaPost={post}
                      isEditing={editingPostId}
                      showCommentsInline={false}
                    />
                  </Carousel.Slide>
                ))}
              </Carousel>
            )}

            {filteredPosts
              .filter((post) => !post.is_roar)
              .map((post) => (
                <PostDetails
                  key={post.post_id}
                  post_id={post.post_id}
                  arenaPost={post}
                  isEditing={!!editingPostId}
                  editingPostId={editingPostId}
                  setEditingPostId={setEditingPostId}
                  ref={post.post_id === postIdToScroll ? targetRef : null}
                  initialCommentToScroll={
                    post.post_id === postIdToScroll
                      ? initialCommentToScroll
                      : null
                  }
                />
              ))}
          </>
        )}
      </div>
    )
  }

  return (
    <>
      {postsList}
      <div className={classes.pagination}>
        <Pagination
          classNames={{ control: classes.control }}
          value={currentPage}
          onChange={handlePageChange}
          total={totalPages}
        />
      </div>
    </>
  )
}
